// Resolves tour targets that may not exist yet: lazy route chunks, data that is
// still loading, or dropdowns that render on demand.

export const targetSelector = (id) => `[data-tour="${id}"]`;

const isVisible = (el) => {
  if (!el || !el.isConnected) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

export const findTarget = (id) => {
  if (typeof document === 'undefined') return null;
  // Several pages can briefly coexist during a route transition; take the
  // first visible match rather than the first in DOM order.
  return [...document.querySelectorAll(targetSelector(id))].find(isVisible) || null;
};

/**
 * Wait until a visible `[data-tour=id]` element exists.
 * Resolves with the element, or null after `timeout` ms or when `signal` aborts.
 */
export function waitForTarget(id, { timeout = 4000, signal } = {}) {
  return new Promise((resolve) => {
    const found = findTarget(id);
    if (found) {
      resolve(found);
      return;
    }

    let done = false;
    let observer = null;
    let timer = null;
    let poll = null;

    const finish = (el) => {
      if (done) return;
      done = true;
      observer?.disconnect();
      clearTimeout(timer);
      clearInterval(poll);
      signal?.removeEventListener('abort', onAbort);
      resolve(el);
    };
    const check = () => {
      const el = findTarget(id);
      if (el) finish(el);
    };
    function onAbort() { finish(null); }

    if (signal?.aborted) {
      finish(null);
      return;
    }
    signal?.addEventListener('abort', onAbort);
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(check);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'data-tour'] });
    }
    // Mutations don't fire for pure layout changes (e.g. a CSS transition that
    // slides the mobile sidebar in), so poll lightly as a backstop.
    poll = setInterval(check, 250);
    timer = setTimeout(() => finish(null), timeout);
  });
}
